import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  Autocomplete,
  Typography,
  Stack,
  Grid2,
  Checkbox,
  FormControlLabel,
  Chip,
  CircularProgress,
  InputAdornment,
} from "@mui/material";
import { toast } from "sonner";
import { Iconify } from "../Minimal/src/components/iconify";
import { TablePaginationCustom } from "../Minimal/src/components/pagination";
import { useTable } from "../Minimal/src/hooks/use-table";
import CommonDatePicker from "../datepicker/CommonDatePicker";
import { format, subMonths } from "date-fns";
import config from "../common/Config";
import { BASE_URL } from "../Config";
import AccountService from "../../services/AccountServices";
import "../common/commonStyles.css";

const formatMoney = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const num = Number(String(value).replace(/,/g, ""));
  if (Number.isNaN(num)) return String(value);
  return num.toLocaleString("en-GB", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const outstandingValue = (row) => {
  const raw = row?.outstanding;
  if (raw === null || raw === undefined || raw === "" || raw === "-") return 0;
  const num = Number(String(raw).replace(/,/g, ""));
  return Number.isNaN(num) ? 0 : num;
};

const CustomerTransactionEnquiry = () => {
  const accessToken = localStorage.getItem("accessToken");
  const token = localStorage.getItem("token");
  const authToken = accessToken || token;

  const [dates, setDates] = useState([subMonths(new Date(), 2), new Date()]);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [customerValue, setCustomerValue] = useState(null);
  const [customerReference, setCustomerReference] = useState("");
  const [searchText, setSearchText] = useState("");
  const [onlyOutstanding, setOnlyOutstanding] = useState(true);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [customerSummary, setCustomerSummary] = useState(null);

  const table = useTable({
    defaultRowsPerPage: 50,
    defaultDense: false,
    defaultOrder: "desc",
    defaultOrderBy: "transaction_date_display",
    defaultSelected: [],
    defaultCurrentPage: 0,
  });

  const fetchCustomerNameOptions = async (inputValue) => {
    if (!inputValue || inputValue.length < 2) return;
    const formData = new FormData();
    formData.append("pattern", inputValue);
    formData.append("platform", "web");
    formData.append("authorization", authToken);
    try {
      const response = await fetch(`${config.API_URL}/ws-get-customer-list`, {
        method: "POST",
        headers: {},
        body: formData,
      });
      const data = await response.json();
      const formattedOptions = Array.isArray(data.customer_details)
        ? data.customer_details.map((item) => ({
            label: `[${item.account_no || item.customer_reference || item.id}] ${item.name}${
              item.postcode ? ` - ${item.postcode}` : ""
            }`,
            value: item.id,
            reference:
              item.account_no ||
              item.customer_reference ||
              item.sage_account ||
              "",
            name: item.name || "",
            short_name: item.short_name || item.shortname || "",
            sage_customer_id: item.sage_customer_id || item.sage_id || "",
            raw: item,
          }))
        : [];
      setCustomerOptions(formattedOptions);
    } catch (error) {
      toast.error("Error fetching customers");
    }
  };

  const extractCustomerSummary = (payload, firstRow) => {
    const customer =
      payload?.customer ||
      payload?.data?.customer ||
      payload?.customer_details ||
      payload?.details?.customer ||
      null;
    const source = customer || firstRow || {};
    return {
      name:
        source.customer_name ||
        source.name ||
        source.company_name ||
        customerValue?.name ||
        "",
      reference:
        source.customer_reference ||
        source.account_no ||
        source.reference ||
        customerReference ||
        customerValue?.reference ||
        "",
      short_name:
        source.short_name ||
        source.shortname ||
        source.short_name_label ||
        customerValue?.short_name ||
        "-",
      credit_limit: source.credit_limit ?? source.credit_limit_amount ?? "-",
      balance: source.balance ?? source.current_balance ?? source.outstanding_balance ?? "-",
      available_credit:
        source.available_credit ?? source.available_credit_amount ?? "-",
    };
  };

  const loadTransactions = async (pageOverride, rowsOverride) => {
    const page = (pageOverride ?? table.page) + 1;
    const perPage = Math.min(rowsOverride ?? table.rowsPerPage, 500);
    const startDate = dates[0] ? format(dates[0], "yyyy-MM-dd") : "";
    const endDate = dates[1] ? format(dates[1], "yyyy-MM-dd") : "";
    const reference =
      (customerReference && customerReference.trim()) ||
      (customerValue && customerValue.reference) ||
      "";
    const customerId = customerValue ? customerValue.value : "";

    if (!reference && !customerId) {
      toast.error("Select a customer or enter customer reference");
      return;
    }

    setLoading(true);
    try {
      const payload = await AccountService.fetchSageCustomerTransactions(
        {
          customer_id: customerId,
          company_id: customerId,
          customer_reference: reference,
          sage_customer_id: customerValue?.sage_customer_id || "",
          start_date: startDate,
          end_date: endDate,
          search: searchText.trim(),
          page,
          per_page: perPage,
        },
        authToken
      );

      const list = Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload?.transactions)
          ? payload.transactions
          : Array.isArray(payload)
            ? payload
            : [];

      const totalCount =
        Number(
          payload?.total ??
            payload?.meta?.total ??
            payload?.pagination?.total ??
            list.length
        ) || list.length;

      setRows(list);
      setTotal(totalCount);
      const summary = extractCustomerSummary(payload, list[0]);
      setCustomerSummary({
        ...summary,
        balance: list.reduce((total, row) => total + outstandingValue(row), 0),
      });
      if (!list.length) {
        toast.info("No transactions found");
      }
    } catch (error) {
      setRows([]);
      setTotal(0);
      toast.error("Failed to load customer transactions");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    table.onChangePage(null, 0);
    loadTransactions(0, table.rowsPerPage);
  };

  useEffect(() => {
    if (accessToken === null && token === null) {
      window.location.href = BASE_URL;
      return;
    }
    document.title = "Customer Transaction Enquiry";
    return () => {
      document.title = "Ebusso";
    };
  }, []);

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      if (onlyOutstanding && Math.abs(outstandingValue(row)) < 0.0001) {
        return false;
      }
      return true;
    });
  }, [rows, onlyOutstanding]);

  const titleName =
    customerSummary?.name ||
    customerValue?.name ||
    customerReference ||
    "Customer";

  const displayStatus = (status) => {
    const raw = String(status || "").trim();
    if (!raw) return "-";
    if (raw.toLowerCase() === "full") return "Paid";
    return raw;
  };

  const statusChip = (status) => {
    const label = displayStatus(status);
    const lower = String(label).toLowerCase();
    let color = "default";
    if (lower.includes("paid") && !lower.includes("un")) color = "success";
    else if (lower.includes("partial")) color = "warning";
    else if (lower.includes("outstanding") || lower.includes("due")) color = "error";
    return (
      <Chip
        label={label}
        size="small"
        color={color === "default" ? undefined : color}
        sx={{
          fontWeight: 600,
          borderRadius: "12px",
          ...(color === "default"
            ? { bgcolor: "rgba(145,158,171,0.16)", color: "#212b36" }
            : {}),
        }}
      />
    );
  };

  return (
    <Box className="page-container">
      <Paper className="paper-style">
        <Typography className="page-title" sx={{ mb: 2 }}>
          Customer Transaction Enquiry for {titleName}
        </Typography>

        <Grid2 container spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Grid2 size={{ xs: 12, md: 5 }}>
            <Autocomplete
              options={customerOptions}
              value={customerValue}
              onChange={(_e, value) => {
                setCustomerValue(value);
                if (value?.reference) setCustomerReference(value.reference);
                if (!value) {
                  setRows([]);
                  setTotal(0);
                  setCustomerSummary(null);
                }
              }}
              onInputChange={(_e, inputValue, reason) => {
                if (reason === "input") fetchCustomerNameOptions(inputValue);
              }}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Customer"
                  placeholder="Search customer (min 2 chars)"
                  size="small"
                />
              )}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 2 }}>
            <TextField
              fullWidth
              size="small"
              label="Customer reference"
              placeholder="e.g. BLA5069"
              value={customerReference}
              onChange={(e) => setCustomerReference(e.target.value.toUpperCase())}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 3 }}>
            <CommonDatePicker className="input-border" value={dates} onChange={setDates} />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 2 }}>
            <Button
              fullWidth
              variant="contained"
              className="button-variant-contained"
              onClick={handleSearch}
              disabled={loading}
              startIcon={
                loading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <Iconify icon="eva:search-fill" width={16} />
                )
              }
            >
              Search
            </Button>
          </Grid2>
        </Grid2>

        {customerSummary && (
          <Paper
            elevation={0}
            sx={{
              border: "1px solid rgba(145, 158, 171, 0.16)",
              borderRadius: 1,
              p: 2,
              mb: 2,
            }}
          >
            <Grid2 container spacing={2}>
              <Grid2 size={{ xs: 6, md: 3 }}>
                <Typography variant="caption" color="text.secondary">
                  Short name
                </Typography>
                <Typography variant="subtitle2">{customerSummary.short_name || "-"}</Typography>
              </Grid2>
              <Grid2 size={{ xs: 6, md: 3 }}>
                <Typography variant="caption" color="text.secondary">
                  Credit limit (£)
                </Typography>
                <Typography variant="subtitle2">
                  {formatMoney(customerSummary.credit_limit)}
                </Typography>
              </Grid2>
              <Grid2 size={{ xs: 6, md: 3 }}>
                <Typography variant="caption" color="text.secondary">
                  Balance (£)
                </Typography>
                <Typography variant="subtitle2">
                  {formatMoney(customerSummary.balance)}
                </Typography>
              </Grid2>
              <Grid2 size={{ xs: 6, md: 3 }}>
                <Typography variant="caption" color="text.secondary">
                  Available credit (£)
                </Typography>
                <Typography variant="subtitle2">
                  {formatMoney(customerSummary.available_credit)}
                </Typography>
              </Grid2>
            </Grid2>
          </Paper>
        )}

        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", md: "center" }}
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <TextField
            size="small"
            placeholder="Search reference / 2nd ref / name"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSearch();
            }}
            sx={{ minWidth: { md: 320 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Iconify icon="eva:search-fill" width={16} />
                </InputAdornment>
              ),
            }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={onlyOutstanding}
                onChange={(e) => setOnlyOutstanding(e.target.checked)}
              />
            }
            label="Only outstanding"
          />
        </Stack>

        <TableContainer sx={{ maxHeight: 560 }}>
          <Table size="small" stickyHeader className="table-cell-border">
            <TableHead>
              <TableRow className="table-cell-header">
                <TableCell>Trans. type</TableCell>
                <TableCell>Trans. date</TableCell>
                <TableCell>Reference</TableCell>
                <TableCell>2nd ref.</TableCell>
                <TableCell align="right">Value</TableCell>
                <TableCell align="right">Allocated</TableCell>
                <TableCell align="right">Outstanding</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>User</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRows.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    No transactions to display
                  </TableCell>
                </TableRow>
              )}
              {filteredRows.map((row, index) => (
                <TableRow
                  key={`${row.reference || "tx"}-${row.transaction_date_display || index}-${index}`}
                  hover
                  sx={{
                    "&:nth-of-type(even)": { bgcolor: "rgba(145,158,171,0.04)" },
                    borderLeft: "3px solid transparent",
                    "&:hover": { borderLeftColor: "#F8991D" },
                  }}
                  className="table-cell-style"
                >
                  <TableCell>{row.transaction_type_label || "-"}</TableCell>
                  <TableCell>{row.transaction_date_display || "-"}</TableCell>
                  <TableCell>{row.reference || "-"}</TableCell>
                  <TableCell>{row.second_reference || "-"}</TableCell>
                  <TableCell align="right">{formatMoney(row.value)}</TableCell>
                  <TableCell align="right">{formatMoney(row.allocated)}</TableCell>
                  <TableCell align="right">{formatMoney(row.outstanding)}</TableCell>
                  <TableCell>{statusChip(row.status)}</TableCell>
                  <TableCell>{row.user_name || "-"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ pt: 2 }}>
          <TablePaginationCustom
            count={total}
            page={table.page}
            rowsPerPage={table.rowsPerPage}
            onPageChange={(event, newPage) => {
              table.onChangePage(event, newPage);
              loadTransactions(newPage, table.rowsPerPage);
            }}
            onRowsPerPageChange={(event) => {
              const next = Number(event.target.value);
              table.onChangeRowsPerPage(event);
              table.onChangePage(null, 0);
              loadTransactions(0, next);
            }}
            rowsPerPageOptions={[25, 50, 100, 200, 500]}
          />
        </Box>
      </Paper>
    </Box>
  );
};

export default CustomerTransactionEnquiry;
